import React, { useState, useEffect, useCallback } from 'react';
import { diariesApi } from '../api';
import DiaryForm from './DiaryForm';
import DiaryItem from './DiaryItem';

interface Diary {
  id: number;
  person_id: number;
  content: string;
  date: string;
  created_at: string;
  images?: DiaryImage[];
}

interface DiaryImage {
  id: number;
  url: string;
  created_at?: string;
}

interface DiaryListProps {
  personId: number;
}

const DiaryList: React.FC<DiaryListProps> = ({ personId }) => {
  const [diaries, setDiaries] = useState<Diary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDiary, setEditingDiary] = useState<Diary | null>(null);

  const loadDiaries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await diariesApi.getAll(personId);
      setDiaries(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error('加载日记失败:', error);
    } finally {
      setLoading(false);
    }
  }, [personId]);

  useEffect(() => {
    void loadDiaries();
  }, [loadDiaries]);

  const handleCreateClick = () => {
    setEditingDiary(null);
    setShowForm(true);
  };

  const handleEditClick = (diary: Diary) => {
    setEditingDiary(diary);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingDiary(null);
  };

  const handleFormSubmit = () => {
    handleFormClose();
    loadDiaries();
  };

  const handleDelete = async (diaryId: number) => {
    if (window.confirm('确定要删除这篇日记吗？')) {
      try {
        await diariesApi.delete(diaryId);
        loadDiaries();
      } catch (error: any) {
        alert('删除失败: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  if (loading) {
    return (
      <div className="dl-loading">
        <div className="dl-loading-spinner" />
        <p>加载日记...</p>
        <style>{`
          .dl-loading {
            display: flex; flex-direction: column; align-items: center;
            padding: 48px 20px; gap: 12px;
          }
          .dl-loading-spinner {
            width: 32px; height: 32px;
            border: 3px solid var(--border); border-top-color: var(--primary);
            border-radius: 50%; animation: dlSpin 0.8s linear infinite;
          }
          @keyframes dlSpin { to { transform: rotate(360deg); } }
          .dl-loading p { color: var(--text-tertiary); font-size: 13px; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="dl-container">
      {/* 头部 */}
      <div className="dl-header">
        <div className="dl-header-left">
          <h3 className="dl-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/>
            </svg>
            日记记录
          </h3>
          <span className="dl-count">{diaries.length} 篇</span>
        </div>
        <button className="dl-add-btn" onClick={handleCreateClick}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14"/><path d="M5 12h14"/>
          </svg>
          写日记
        </button>
      </div>

      {showForm && (
        <DiaryForm
          personId={personId}
          diary={editingDiary}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}

      {diaries.length === 0 ? (
        <div className="dl-empty">
          <div className="dl-empty-icon">📝</div>
          <h3 className="dl-empty-title">还没有日记记录</h3>
          <p className="dl-empty-desc">记录与 TA 的互动和感受，AI 将帮你分析性格特征</p>
          <button className="dl-empty-btn" onClick={handleCreateClick}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14"/><path d="M5 12h14"/>
            </svg>
            写第一篇日记
          </button>
        </div>
      ) : (
        <div className="dl-list">
          {diaries.map((diary) => (
            <DiaryItem
              key={diary.id}
              diary={diary}
              onEdit={handleEditClick}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <style>{`
        .dl-container {
          animation: dlFadeIn 0.3s var(--ease-out);
        }

        @keyframes dlFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .dl-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .dl-header-left {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .dl-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .dl-count {
          font-size: 12px;
          color: var(--text-tertiary);
          font-weight: 500;
          padding: 2px 10px;
          background: var(--surface-hover);
          border-radius: var(--radius-full);
        }

        .dl-add-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 9px 18px;
          border: none;
          border-radius: var(--radius-xl);
          background: var(--primary);
          color: white;
          font-size: 13px;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          box-shadow: var(--shadow-primary);
          transition: all var(--duration-normal) var(--ease-out);
        }

        .dl-add-btn:active {
          transform: scale(0.95);
        }

        .dl-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        /* Empty State */
        .dl-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 48px 24px;
          text-align: center;
        }

        .dl-empty-icon {
          font-size: 44px;
          margin-bottom: 12px;
          opacity: 0.8;
        }

        .dl-empty-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 6px;
        }

        .dl-empty-desc {
          font-size: 13px;
          color: var(--text-tertiary);
          margin: 0 0 20px;
          max-width: 260px;
          line-height: 1.6;
        }

        .dl-empty-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 11px 22px;
          border: none;
          border-radius: var(--radius-xl);
          background: var(--primary);
          color: white;
          font-size: 14px;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          box-shadow: var(--shadow-primary);
          transition: all var(--duration-normal) var(--ease-out);
        }

        .dl-empty-btn:active {
          transform: scale(0.96);
        }
      `}</style>
    </div>
  );
};

export default DiaryList;
